import { Input } from "@components/ui/input";
import { PlusIcon, SearchIcon, XIcon } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import LoadingAnimation from "@assets/svg/LoadingAnimation";
import { useSnackbar } from "notistack";
import { RecruiterTypes } from "@utils/constants";
import {
  approvalRequest,
  updateRecruiterInvitationSso,
  accountMembers,
} from "@endpoints/recruiter.service";
import dayjs from "dayjs";
import { RecruiterApprovalStatus } from "@utils/constants";
import InviteAccountMemberModal from "../components/InviteAccountMemberModal";

type RecruiterInvitation = {
  _id: string;
  agencyId?: string;
  firstName: string;
  lastName: string;
  company: string;
  jobTitle?: string;
  phone?: string;
  email: string;
  address1: string;
  address2?: string;
  city?: string;
  country: string;
  postCode?: string;
  role: number;
  status: "Pending" | "Accepted";
  ssoEnabled?: boolean;
  refNo?: string;
  userRegisterAt?: string;
  lastActive?: string;
  recruiterApprovalStatus?: number;
};

export const roleOptions = [
  { label: "Admin", value: RecruiterTypes.ADMIN },
  { label: "Manager", value: RecruiterTypes.SUBADMIN },
  { label: "Staff member", value: RecruiterTypes.MEMBER },
];

const AccountMembers = () => {
  const { enqueueSnackbar } = useSnackbar();
  const sentinelRef = useRef(null);
  const scrollListRef = useRef(null);

  // Local state
  const [searchText, setSearchText] = useState("");
  const [page, setPage] = useState(1);
  const [hasMoreData, setHasMoreData] = useState(true);
  const [isLoadingMoreData, setIsLoadingMoreData] = useState(false);

  const [invitations, setInvitations] = useState<RecruiterInvitation[]>([]);
  const [isInvitesLoading, setIsInvitesLoading] = useState(false);
  const [ssoUpdatingId, setSsoUpdatingId] = useState<string | null>(null);
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [selectedApprovalInvite, setSelectedApprovalInvite] =
    useState<RecruiterInvitation | null>(null);
  const [approvalActionLoading, setApprovalActionLoading] = useState<
    "approve" | "reject" | null
  >(null);

  const filteredInvitations = useMemo(() => {
    if (!searchText?.trim()) return invitations;
    const term = searchText.toLowerCase();
    return invitations.filter((invite) => {
      const name = `${invite.firstName} ${invite.lastName}`.toLowerCase();
      return (
        name.includes(term) ||
        invite.email.toLowerCase().includes(term) ||
        (invite.refNo || "").toLowerCase().includes(term)
      );
    });
  }, [invitations, searchText]);

  const fetchAccountMembers = useCallback(async () => {
    try {
      if (page === 1 || searchText) {
        setIsInvitesLoading(true);
      }
      const { success, data } = await accountMembers({ page, searchText });
      if (success) {
        setHasMoreData(data?.hasMore);
        setInvitations((prev) =>
          page > 1 ? [...prev, ...data?.members] : data?.members || [],
        );
      }
    } catch (error) {
      enqueueSnackbar(error || "Failed to fetch invitations", {
        variant: "error",
      });
    } finally {
      setIsInvitesLoading(false);
      setIsLoadingMoreData(false);
    }
  }, [page, searchText]);

  const handleToggleSso = async (inviteId: string, nextValue: boolean) => {
    try {
      setSsoUpdatingId(inviteId);
      const { success, data } = await updateRecruiterInvitationSso({
        inviteId,
        ssoEnabled: nextValue,
      });
      if (success) {
        setInvitations((prev) =>
          prev.map((item) => (item._id === inviteId ? data : item)),
        );
      }
    } catch (error) {
      enqueueSnackbar(error || "Failed to update SSO", { variant: "error" });
    } finally {
      setSsoUpdatingId(null);
    }
  };

  const handleOpenApprovalModal = (invite: RecruiterInvitation) => {
    setSelectedApprovalInvite(invite);
  };

  const handleCloseApprovalModal = () => {
    setSelectedApprovalInvite(null);
    setApprovalActionLoading(null);
  };

  const handleApprovalAction = async (status: 1 | 2) => {
    const recruiterId =
      selectedApprovalInvite?.agencyId || selectedApprovalInvite?._id;
    if (!recruiterId) return;
    try {
      setApprovalActionLoading(status === 1 ? "approve" : "reject");
      const data = await approvalRequest({
        recruiterId,
        status,
      });

      if (data.success) {
        enqueueSnackbar(data.data.message, {
          variant: "success",
        });
        handleCloseApprovalModal();
        await fetchAccountMembers();
      }
    } catch (error) {
      enqueueSnackbar(error || "Failed to update approval request", {
        variant: "error",
      });
    } finally {
      setApprovalActionLoading(null);
    }
  };

  const handleLoadMore = useCallback(() => {
    if (!hasMoreData || isLoadingMoreData) return;
    setIsLoadingMoreData(true);
    setPage((prev) => prev + 1);
  }, [hasMoreData, page, isLoadingMoreData]);

  useEffect(() => {
    if (!sentinelRef?.current || !scrollListRef?.current) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries?.[0]?.isIntersecting) {
          handleLoadMore();
        }
      },
      {
        root: scrollListRef?.current,
        rootMargin: "0px 0px 10px 0px",
        threshold: 0,
      },
    );
    observer.observe(sentinelRef?.current);
    return () => observer.disconnect();
  }, [handleLoadMore, filteredInvitations?.length]);

  useEffect(() => {
    fetchAccountMembers();
  }, [fetchAccountMembers]);

  console.log("filteredInvitations", filteredInvitations);

  return (
    <div className="flex flex-col flex-1 min-h-0">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <p className="text-sm font-semibold">Invited Members</p>
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center bg-white pr-3 border rounded-md w-72">
            <Input
              className="!focus-visible:ring-[3px] border-none !text-xs font-medium"
              placeholder="Search by name, email, reference..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
            />
            <SearchIcon className="w-4 h-4" color="#2e2e2e" />
          </div>
          <button
            type="button"
            className="flex items-center justify-center rounded-md py-2 px-3 gap-2 bg-secondary hover:bg-orange-400 text-white font-semibold text-xs"
            onClick={() => setIsInviteModalOpen(true)}
          >
            <PlusIcon className="w-4 h-4" color="white" />
            Invite Member
          </button>
        </div>
      </div>

      {isInvitesLoading ? (
        <div className="flex items-center justify-center py-10">
          <LoadingAnimation className="h-6 w-6" color="#2e2e2e" />
        </div>
      ) : filteredInvitations.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-2 py-10">
          <span className="font-medium text-sm text-light-black">
            No invited members available.
          </span>
        </div>
      ) : (
        // Table section
        <div className="flex flex-col flex-1 min-h-0">
          {/* Table header */}
          <div className="grid grid-cols-[1.2fr_1fr_0.8fr_0.6fr_0.6fr] border-b pb-3">
            <span className="font-bold text-xs text-light-black">Member</span>
            <span className="font-bold text-xs text-light-black">Role</span>
            <span className="font-bold text-xs text-light-black">
              RegisterAt
            </span>
            <span className="font-bold text-xs text-light-black text-center">
              Approval
            </span>
            <span className="font-bold text-xs text-light-black text-center">
              SSO
            </span>
          </div>
          {/* Table body */}
          <div
            ref={scrollListRef}
            className="flex flex-col flex-1 min-h-0 overflow-y-scroll no-scrollbar"
          >
            {filteredInvitations.map((invite) => {
              const roleLabel =
                roleOptions.find((opt) => opt.value === invite.role)?.label ||
                "-";
              const approvalStatus = invite.recruiterApprovalStatus;
              const recruiterId = invite.agencyId || invite._id;
              const approvalLabel =
                approvalStatus === RecruiterApprovalStatus.ACCEPTED
                  ? "Approved"
                  : approvalStatus === RecruiterApprovalStatus.REJECTED
                    ? "Rejected"
                    : approvalStatus === RecruiterApprovalStatus.REQUESTED
                      ? "Pending approval"
                      : invite.status === "Accepted"
                        ? "Pending approval"
                        : "Invitation pending";
              const approvalClass =
                approvalStatus === RecruiterApprovalStatus.ACCEPTED
                  ? "text-[#1e8528] bg-[#dcf7d6]"
                  : approvalStatus === RecruiterApprovalStatus.REJECTED
                    ? "text-[#d72417] bg-[#fbd9ce]"
                    : "text-[#A43370] bg-[#F7DEEC]";
              return (
                <div
                  key={invite._id}
                  className="grid grid-cols-[1.2fr_1fr_0.8fr_0.6fr_0.6fr] py-4 border-b"
                >
                  <div className="flex flex-col">
                    <span className="font-medium text-xs text-light-black">
                      {invite.firstName} {invite.lastName}
                    </span>
                    <span className="text-[11px] text-gray-500">
                      {invite.email}
                    </span>
                  </div>
                  <span className="font-medium text-xs text-light-black flex items-center capitalize">
                    {roleLabel}
                  </span>
                  <span className="font-medium text-xs text-light-black flex items-center">
                    {invite?.userRegisterAt
                      ? dayjs(invite.userRegisterAt).format("YYYY-MM-DD")
                      : "-"}
                  </span>
                  <span className="font-medium text-xs text-light-black flex items-center justify-center">
                    {approvalStatus === RecruiterApprovalStatus.REQUESTED ? (
                      <button
                        type="button"
                        className={`rounded px-3 py-1 text-xs ${approvalClass} cursor-pointer`}
                        onClick={() =>
                          recruiterId ? handleOpenApprovalModal(invite) : null
                        }
                      >
                        {approvalLabel}
                      </button>
                    ) : (
                      <span
                        className={`rounded px-3 py-1 text-xs ${approvalClass}`}
                      >
                        {approvalLabel}
                      </span>
                    )}
                  </span>
                  <div className="flex items-center justify-center">
                    <input
                      type="checkbox"
                      checked={!!invite.ssoEnabled}
                      disabled={ssoUpdatingId === invite._id}
                      onChange={(e) =>
                        handleToggleSso(invite._id, e.target.checked)
                      }
                    />
                  </div>
                </div>
              );
            })}

            {/* Loading more data loader */}
            {
              isLoadingMoreData || true?
              <div className="flex items-center justify-center gap-2 text-light-black italic font-medium text-xs">
                <LoadingAnimation color="black"/>
                Loading more data...
              </div>
              :
              null
            }
            {/* SentinelRef */}
            <div ref={sentinelRef} className="h-px shrink-0" aria-hidden />
          </div>
        </div>
      )}

      <InviteAccountMemberModal
        openModal={isInviteModalOpen}
        setOpenModal={setIsInviteModalOpen}
      />

      {selectedApprovalInvite ? (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/40 px-4">
          <div className="bg-white w-full max-w-2xl rounded-lg border border-gray-200 shadow-xl">
            <div className="flex items-center justify-between border-b px-6 py-4">
              <div>
                <p className="text-sm font-semibold">
                  Recruiter approval request
                </p>
                <p className="text-xs text-gray-500">
                  Review the inviter details before approving or rejecting.
                </p>
              </div>
              <button
                type="button"
                className="p-1 text-gray-500 hover:text-gray-700"
                onClick={handleCloseApprovalModal}
              >
                <XIcon className="h-4 w-4" />
              </button>
            </div>

            <div className="px-6 py-5 grid grid-cols-2 gap-4 text-xs">
              <div className="flex flex-col gap-1">
                <span className="font-semibold text-gray-500">Name</span>
                <span className="text-gray-900">
                  {selectedApprovalInvite.firstName}{" "}
                  {selectedApprovalInvite.lastName}
                </span>
              </div>
              <div className="flex flex-col gap-1">
                <span className="font-semibold text-gray-500">Email</span>
                <span className="text-gray-900">
                  {selectedApprovalInvite.email}
                </span>
              </div>
              <div className="flex flex-col gap-1">
                <span className="font-semibold text-gray-500">Company</span>
                <span className="text-gray-900">
                  {selectedApprovalInvite.company || "-"}
                </span>
              </div>
              <div className="flex flex-col gap-1">
                <span className="font-semibold text-gray-500">Role</span>
                <span className="text-gray-900">
                  {roleOptions.find(
                    (opt) => opt.value === selectedApprovalInvite.role,
                  )?.label || "-"}
                </span>
              </div>
              <div className="flex flex-col gap-1">
                <span className="font-semibold text-gray-500">Registered</span>
                <span className="text-gray-900">
                  {selectedApprovalInvite.userRegisterAt
                    ? dayjs(selectedApprovalInvite.userRegisterAt).format(
                        "YYYY-MM-DD",
                      )
                    : "-"}
                </span>
              </div>
              <div className="flex flex-col gap-1">
                <span className="font-semibold text-gray-500">
                  Approval status
                </span>
                <span className="text-gray-900">
                  {selectedApprovalInvite.recruiterApprovalStatus ===
                  RecruiterApprovalStatus.REQUESTED
                    ? "Pending approval"
                    : selectedApprovalInvite.recruiterApprovalStatus ===
                        RecruiterApprovalStatus.ACCEPTED
                      ? "Approved"
                      : selectedApprovalInvite.recruiterApprovalStatus ===
                          RecruiterApprovalStatus.REJECTED
                        ? "Rejected"
                        : "Invitation pending"}
                </span>
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 border-t px-6 py-4">
              <button
                type="button"
                className="rounded-md border border-gray-200 px-4 py-2 text-xs font-medium text-gray-700 hover:bg-gray-100"
                onClick={handleCloseApprovalModal}
              >
                Cancel
              </button>
              <button
                type="button"
                className="rounded-md border border-[#d72417] px-4 py-2 text-xs font-medium text-[#d72417] hover:bg-[#fbd9ce]"
                onClick={() => handleApprovalAction(2)}
                disabled={
                  approvalActionLoading !== null ||
                  !selectedApprovalInvite?.agencyId
                }
              >
                {approvalActionLoading === "reject" ? "Rejecting..." : "Reject"}
              </button>
              <button
                type="button"
                className="flex items-center justify-center rounded-md py-2 px-4 gap-2 bg-green-600 hover:bg-green-700 text-white font-semibold text-xs"
                onClick={() => handleApprovalAction(1)}
                disabled={
                  approvalActionLoading !== null ||
                  !selectedApprovalInvite?.agencyId
                }
              >
                {approvalActionLoading === "approve" ? (
                  <LoadingAnimation className="w-4 h-4" color="white" />
                ) : null}
                Approve
              </button>
            </div>
            {!selectedApprovalInvite?.agencyId ? (
              <div className="px-6 pb-4 text-xs text-amber-700">
                This invitation has not yet been linked to a recruiter user id,
                so approval cannot be submitted yet.
              </div>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default AccountMembers;